/** Social Composer canvas renderer — composites background + design + text +
 * logo onto a canvas at exact platform pixels. Read-only over the composer
 * state (passed in explicitly); all state mutation lives in social-composer.js. */

/**
 * Render the composition onto the on-screen canvas at full platform pixels;
 * CSS scales it down to fit the stage. Shared by preview + export.
 * @param {HTMLCanvasElement} canvas — target canvas (resized to platform pixels)
 * @param {object} state — composer state (platform/bg/image/text/logo fields), read-only here
 */
export function drawComposition(canvas, state) {
  const { w, h } = state.platform
  canvas.width = w; canvas.height = h
  const ctx = canvas.getContext('2d')

  // 1) Background
  ctx.fillStyle = state.bg
  ctx.fillRect(0, 0, w, h)
  if (state.bgMode === 'ai' && state.aiBg) {
    drawCover(ctx, state.aiBg, 0, 0, w, h) // AI scene fills the frame
  }

  // 2) Design layer
  if (state.image) {
    if (state.bgMode === 'fill') {
      drawCover(ctx, state.image, 0, 0, w, h)
    } else {
      // Brand / AI mode: the design is 'contained' in the band left between the
      // reserved logo zone (top/bottom) and the text zone (top/bottom), so the
      // logo + text never sit on top of the design.
      const pad = w * 0.08
      let top = h * (state.logo === 'top' ? 0.12 : 0.05)
      let bot = h * (state.logo === 'bottom' ? 0.88 : 0.95)
      if (state.position === 'top') top = Math.max(top, h * 0.26)
      if (state.position === 'bottom') bot = Math.min(bot, h * 0.74)
      if (state.position === 'center') { top = Math.max(top, h * 0.18); bot = Math.min(bot, h * 0.82) }
      drawContain(ctx, state.image, pad, top, w - pad * 2, bot - top)
    }
  }

  // 3) Text overlay (eyebrow + headline + subtext), brand font + legibility shadow.
  drawText(ctx, w, h, state)

  // 4) Logo — the mamamiya wordmark, stamped top/bottom centre (pure text).
  drawLogo(ctx, w, h, state)
}

// The rgba() shadow literals below are image-content colors, not themed UI
// chrome — they are baked into the exported PNG pixels and must not follow the
// theme (a legibility shadow behind text is black regardless of app theme).
function drawText(ctx, w, h, state) {
  const eyeb = (state.eyebrow || '').trim().toUpperCase()
  const head = (state.headline || '').trim()
  const sub = (state.subtext || '').trim()
  if (!eyeb && !head && !sub) return

  const margin = w * 0.08
  const maxW = w - margin * 2
  const eyebSize = Math.round(w * 0.036)
  const headSize = Math.round(w * 0.075)
  const subSize = Math.round(w * 0.042)
  const lineGap = headSize * 0.22

  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.shadowColor = 'rgba(0,0,0,0.45)'
  ctx.shadowBlur = w * 0.012
  ctx.shadowOffsetY = w * 0.003

  ctx.font = `700 ${headSize}px "${state.font}", sans-serif`
  const headLines = head ? wrap(ctx, head, maxW) : []
  ctx.font = `400 ${subSize}px "${state.font}", sans-serif`
  const subLines = sub ? wrap(ctx, sub, maxW) : []

  const blockH =
    (eyeb ? eyebSize + lineGap * 1.2 : 0) +
    headLines.length * (headSize + lineGap) +
    (subLines.length ? lineGap * 1.5 + subLines.length * (subSize + lineGap) : 0)

  let y
  if (state.position === 'top') y = h * 0.12 + (eyeb ? eyebSize : headSize)
  else if (state.position === 'center') y = (h - blockH) / 2 + (eyeb ? eyebSize : headSize)
  else y = h - margin - blockH + (eyeb ? eyebSize : headSize) // bottom

  ctx.fillStyle = state.color
  if (eyeb) {
    ctx.font = `600 ${eyebSize}px "${state.font}", sans-serif`
    ctx.letterSpacing = `${Math.round(eyebSize * 0.14)}px`
    ctx.fillText(eyeb, w / 2, y)
    ctx.letterSpacing = '0px'
    y += eyebSize + lineGap * 1.2
  }
  ctx.font = `700 ${headSize}px "${state.font}", sans-serif`
  for (const line of headLines) { ctx.fillText(line, w / 2, y); y += headSize + lineGap }
  if (subLines.length) {
    y += lineGap * 0.5
    ctx.font = `400 ${subSize}px "${state.font}", sans-serif`
    for (const line of subLines) { ctx.fillText(line, w / 2, y); y += subSize + lineGap }
  }
  ctx.shadowColor = 'transparent'
}

function drawLogo(ctx, w, h, state) {
  if (state.logo === 'off') return
  const size = Math.round(w * 0.046)
  const margin = w * 0.06
  ctx.save()
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.shadowColor = 'rgba(0,0,0,0.4)'
  ctx.shadowBlur = w * 0.01
  ctx.fillStyle = state.color
  ctx.font = `700 ${size}px "Bricolage Grotesque", sans-serif`
  const y = state.logo === 'top' ? margin + size : h - margin
  ctx.fillText('mamamiya', w / 2, y)
  ctx.restore()
}

function wrap(ctx, text, maxW) {
  const words = text.split(/\s+/)
  const lines = []
  let cur = ''
  for (const word of words) {
    const test = cur ? `${cur} ${word}` : word
    if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = word }
    else cur = test
  }
  if (cur) lines.push(cur)
  return lines
}

function drawCover(ctx, img, dx, dy, dw, dh) {
  const r = Math.max(dw / img.width, dh / img.height)
  const sw = dw / r, sh = dh / r
  const sx = (img.width - sw) / 2, sy = (img.height - sh) / 2
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh)
}

function drawContain(ctx, img, dx, dy, dw, dh) {
  const r = Math.min(dw / img.width, dh / img.height)
  const w = img.width * r, h = img.height * r
  ctx.drawImage(img, dx + (dw - w) / 2, dy + (dh - h) / 2, w, h)
}
